import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Drawer, Modal, Typography, IconButton } from '@mui/material';
import CustomModal from '../../../../common/components/Modal/Modal';
import { httpService } from '../../../../utility/httpService';
import { httpConstants } from '../../../../constants';
import { API_BASE, API_GRAPH_BASE } from '../../../../config/env';
import { useGraphQLQuery } from '../../../../customHooks/useGraphQL';
import { GET_AI_SOLUTIONS_DATA } from '../../../../graphql/queries/commonQueries';
import Loader from '../../../../common/components/Loader';
import ViewSolutionDetails from '../../../SimulationXPLR/ViewSolutionDetails';
import ShowToast, { ToastTypeEnum } from '../../../../common/components/ShowToast';
import { refreshTaxonomy } from '../../../../slices/diagramViewerSlice';
import { updateIdeationFieldState } from '../../../../slices/ideationSlice/newIdeationSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import IdeationOpportunitiesCard from '../../../../common/components/IdeationOpportunities/ideationAiOpportunitiesCard';
import IdeationMenu from '../../../../common/components/IdeationMenu/IdeationMenu';
import ConfirmationModal from '../../../../common/components/ConfirmationModal';
import { Close as CloseIcon } from '@mui/icons-material';
import { handleScrollToTop } from '../../../SimulationXPLR/utils';
import {
  fetchAISolutionByID,
  fetchAISolutionWithAgentEnablement,
} from '../../../../utility/aiOpportunityUtils';
import type { Enhancement } from '../proposedEnhancements';

import {
  convertBpmnXmlToBase64,
  generateWordWithAccurateTOC,
  updateWordDocLinks,
} from '../../../../utility/reportUtils';
import DownloadProgressSnackbar from '../../../../common/components/DownloadProgressSnackbar';
import { REPORT_PROCESS_DIAGRAM_REPLACABLE_LINK } from '../../../../appConstants';

type AiSolutionRow = {
  id: string;
  serial: number;
  opportunity_name: string;
  impact?: string | null;
  roi?: string | null | React.ReactElement;
  complexity?: string | null | React.ReactElement;
  vault?: boolean;
  solution_enabler?: string | null;
  approval_status?: 'pending' | 'approved' | null;
  description?: string | null;
};

interface AiOpportunitiesProps {
  diagramId: string;
  selectedCard?: any;
  refreshKey?: number;
  isLoading?: boolean;
  setActiveStep?: React.Dispatch<React.SetStateAction<string>>;
  enhancements?: Enhancement[];
  isProcess?: boolean;
}

const AiOpportunities: React.FC<AiOpportunitiesProps> = ({
  diagramId,
  selectedCard,
  refreshKey,
  isLoading = false,
  setActiveStep,
  // enhancements,
  isProcess = false,
}) => {
  const currentOrg = localStorage.getItem('current_organization') || 'Empty';

  const [solutionData, setSolutionData] = useState<AiSolutionRow[]>([]);
  const [, setOriginalSolutionData] = useState<AiSolutionRow[]>([]);
  const [dataSourceType, setDataSourceType] = useState<'rest' | 'graphql' | null>(null);
  const [selectedSolutionFromLibrary, setSelectedSolutionFromLibrary] = useState<{
    id: string;
    name: string;
    vault?: boolean;
    solution_enabler?: string;
  }>({ id: '', name: '' });
  const [aiSolutionData, setAiSolutionData] = useState<any>(null);
  const [showVaultModal, setShowVaultModal] = useState<boolean>(false);
  const [enterpriseContextApplied, setEnterpriseContextApplied] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [solutionToDelete] = useState<string>('');
  const [internalLoading, setInternalLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const { selectedProcess } = useSelector((state: RootState) => state.newIdeation);

  const useCaseId = useSelector((state: RootState) => state.newIdeation.id);
  const selectedCardUseCaseId =
    selectedCard?.useCaseId || selectedCard?.use_case_id || selectedCard?.id;
  const combinedLoading = isLoading || internalLoading;
  const actualDiagramId = selectedCard?.diagramId || diagramId;
  const [showEditConfirmModal, setShowEditConfirmModal] = useState<boolean>(false);
  const [needsRefetchOnClose, setNeedsRefetchOnClose] = useState<boolean>(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState<boolean>(false);
  const [renameProcessName, setRenameProcessName] = useState<string>(selectedProcess || '');
  const [currentProcessName, setCurrentProcessName] = useState<string>(
    selectedCard?.name || selectedProcess || ''
  );
  const [loadingDocument, setLoadingDocument] = useState<boolean>(false);
  const [downloadProgressOpen, setDownloadProgressOpen] = useState<boolean>(false);
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const formState = useSelector((state: RootState) => state.newIdeation);

  const { data, loading: qlLoading } = useGraphQLQuery(
    GET_AI_SOLUTIONS_DATA,
    { v1: { subprocessId: `${selectedCard?.id}` }, v2: ['name'] },
    { skip: !selectedCard?.id }
  );

  const [primarySort] = useState<'roi' | 'complexity'>('roi');
  const [filterValues, setFilterValues] = useState({
    roi: { high: false, medium: false, low: false },
    complexity: { high: false, medium: false, low: false },
    type: { transformative: false, breakthrough: false, incremental: false },
  });
  // Track applied filters separately from temporary UI selections
  const [appliedFilters, setAppliedFilters] = useState({
    roi: { high: false, medium: false, low: false },
    complexity: { high: false, medium: false, low: false },
    type: { transformative: false, breakthrough: false, incremental: false },
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [filteredData, setFilteredData] = useState<AiSolutionRow[]>([]);
  const { diagramData } = useSelector((state: RootState) => state.diagramViewer);

  // Update the filter button onClick
  const handleOpenFilterDrawer = () => setShowFilterDrawer(true);
  const handleCloseFilterDrawer = () => setShowFilterDrawer(false);

  // Add handlers for filter changes
  const handleFilterChange = (category: 'roi' | 'complexity' | 'type', option: string) => {
    setFilterValues((prev) => {
      if (category === 'roi' || category === 'complexity') {
        const key = option.toLowerCase() as 'high' | 'medium' | 'low';
        return {
          ...prev,
          [category]: {
            ...prev[category as 'roi' | 'complexity'],
            [key]: !prev[category][key],
          },
        };
      } else {
        const key = option.toLowerCase() as 'transformative' | 'breakthrough' | 'incremental';
        return {
          ...prev,
          type: {
            ...prev.type,
            [key]: !prev.type[key],
          },
        };
      }
    });
  };

  const handleClearAll = async () => {
    setFilterValues({
      roi: { high: false, medium: false, low: false },
      complexity: { high: false, medium: false, low: false },
      type: { transformative: false, breakthrough: false, incremental: false },
    });
    // Clear applied filters
    setAppliedFilters({
      roi: { high: false, medium: false, low: false },
      complexity: { high: false, medium: false, low: false },
      type: { transformative: false, breakthrough: false, incremental: false },
    });

    // Fetch all data without filters
    setInternalLoading(true);
    try {
      const response = await fetchAISolution();
      if (response?.results?.length > 0) {
        const mapped = response.results.map(
          (
            {
              id,
              opportunity_name,
              roi,
              complexity,
              impact,
              vault,
              solution_enabler,
              status,
              description,
            }: any,
            index: number
          ) => ({
            id,
            serial: index + 1,
            opportunity_name,
            roi: roi || '-',
            complexity: complexity || '-',
            impact: impact || '-',
            vault: vault || false,
            solution_enabler: solution_enabler || 'N/A',
            approval_status: status || null,
            description: description || null,
          })
        );

        setOriginalSolutionData(mapped);
        setFilteredData(mapped);
        sortSolutions(mapped);
        setDataSourceType('rest');
      } else {
        setOriginalSolutionData([]);
        setFilteredData([]);
        setSolutionData([]);
        setDataSourceType('rest');
      }
    } catch (error) {
      console.error('Error clearing filters:', error);
      ShowToast({ message: 'Error clearing filters', type: ToastTypeEnum.Error });
      setSolutionData([]);
    } finally {
      setInternalLoading(false);
      setShowFilterDrawer(false);
    }
  };

  const handleAddFilter = async () => {
    // Save current filter values as applied filters
    setAppliedFilters(filterValues);

    setInternalLoading(true);
    try {
      // Fetch data with applied filters
      const response = await fetchAISolution(filterValues);
      if (response?.results?.length > 0) {
        const mapped = response.results.map(
          (
            {
              id,
              opportunity_name,
              roi,
              complexity,
              impact,
              vault,
              solution_enabler,
              status,
              description,
            }: any,
            index: number
          ) => ({
            id,
            serial: index + 1,
            opportunity_name,
            roi: roi || '-',
            complexity: complexity || '-',
            impact: impact || '-',
            vault: vault || false,
            solution_enabler: solution_enabler || 'N/A',
            approval_status: status || null,
            description: description || null,
          })
        );

        setOriginalSolutionData(mapped);
        setFilteredData(mapped);
        sortSolutions(mapped);
        setDataSourceType('rest');
      } else {
        setOriginalSolutionData([]);
        setFilteredData([]);
        setSolutionData([]);
        setDataSourceType('rest');
      }
    } catch (error) {
      console.error('Error applying filters:', error);
      ShowToast({ message: 'Error applying filters', type: ToastTypeEnum.Error });
      setSolutionData([]);
    } finally {
      setInternalLoading(false);
      setShowFilterDrawer(false);
    }
  };

  const extractTextFromReactNode = (node: any): string => {
    if (node == null) return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (typeof node === 'boolean') return '';
    if (node.props && node.props.children) {
      const children = node.props.children;
      if (Array.isArray(children)) return children.map(extractTextFromReactNode).join(' ');
      return extractTextFromReactNode(children);
    }
    if (Array.isArray(node)) return node.map(extractTextFromReactNode).join(' ');
    try {
      return String(node);
    } catch {
      return '';
    }
  };

  const normalizeValue = (val: any): string => {
    if (!val && val !== 0) return '';
    if (typeof val === 'string') return val.trim().toUpperCase();
    // handle JSX nodes which you sometimes store in solutionData
    const text = extractTextFromReactNode(val);
    return text.trim().toUpperCase();
  };

  // Priorities (base)
  const ROI_PRIORITY_BASE = ['HIGH', 'MEDIUM', 'LOW'];
  const COMPLEXITY_PRIORITY_BASE = ['LOW', 'MEDIUM', 'HIGH'];

  const buildPriorityOrder = () => {
    // primary = 'roi' => primaryOrder = ROI_PRIORITY_BASE (default High->Low)
    // but reversePrimary flips the primary order
    // secondary must follow the spec:
    // - if ROI primary, secondary complexity order: Low -> Medium -> High
    // - if Complexity primary, secondary ROI order: High -> Medium -> Low
    let roiOrder = [...ROI_PRIORITY_BASE];
    let complexityOrder = [...COMPLEXITY_PRIORITY_BASE];

    if (primarySort === 'roi') {
      roiOrder = [...ROI_PRIORITY_BASE]; // base
      // ensure ROI inside complexity default is High -> Low (as per spec)
      roiOrder = ['HIGH', 'MEDIUM', 'LOW'];
    }

    return { roiOrder, complexityOrder };
  };

  // Main sorting function used to sort and then set solutionData
  const sortSolutions = (rows: AiSolutionRow[]) => {
    const { roiOrder, complexityOrder } = buildPriorityOrder();

    const roiIndex = (v: string) => {
      const idx = roiOrder.indexOf(v);
      return idx === -1 ? roiOrder.length + 1 : idx;
    };
    const complexityIndex = (v: string) => {
      const idx = complexityOrder.indexOf(v);
      return idx === -1 ? complexityOrder.length + 1 : idx;
    };

    const sorted = [...rows].sort((a, b) => {
      const aRoi = normalizeValue(a.roi || '');
      const bRoi = normalizeValue(b.roi || '');
      const aComplexity = normalizeValue(a.complexity || '');
      const bComplexity = normalizeValue(b.complexity || '');

      if (primarySort === 'roi') {
        // Compare ROI by roiOrder
        const cmpRoi = roiIndex(aRoi) - roiIndex(bRoi);
        if (cmpRoi !== 0) return cmpRoi;
        // same ROI -> compare complexity by complexityOrder
        const cmpComplex = complexityIndex(aComplexity) - complexityIndex(bComplexity);
        if (cmpComplex !== 0) return cmpComplex;
      } else {
        // primary = complexity
        const cmpComplex = complexityIndex(aComplexity) - complexityIndex(bComplexity);
        if (cmpComplex !== 0) return cmpComplex;
        // same complexity -> compare roi by roiOrder
        const cmpRoi = roiIndex(aRoi) - roiIndex(bRoi);
        if (cmpRoi !== 0) return cmpRoi;
      }

      // final tie-breaker: alphabetical by opportunity_name
      const nameA = String(a.opportunity_name || '').toLowerCase();
      const nameB = String(b.opportunity_name || '').toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

    setSolutionData(sorted);
  };

  const fetchAISolution = useCallback(
    async (filters?: any): Promise<any> => {
      try {
        let url = `${API_BASE}/api/v1/e2e_taxonomy/opportunities/?diagram_id=${actualDiagramId}`;
        const queryParams: string[] = [];

        if (filters) {
          // Add ROI filters - convert to uppercase as per API expectations
          const roiFilters = Object.keys(filters.roi)
            .filter((key) => filters.roi[key])
            .map((key) => key.toUpperCase());
          if (roiFilters.length > 0) {
            queryParams.push(`roi=${roiFilters.join(',')}`);
          }

          // Add Complexity filters - convert to uppercase as per API expectations
          const complexityFilters = Object.keys(filters.complexity)
            .filter((key) => filters.complexity[key])
            .map((key) => key.toUpperCase());
          if (complexityFilters.length > 0) {
            queryParams.push(`complexity=${complexityFilters.join(',')}`);
          }
          const impactFilters = Object.keys(filters.type)
            .filter((key) => filters.type[key])
            .map((key) => {
              if (key === 'transformative') return 'Transformative';
              if (key === 'breakthrough') return 'Breakthrough';
              if (key === 'incremental') return 'Incremental';
              return key;
            });
          if (impactFilters.length > 0) {
            queryParams.push(`impactType=${impactFilters.join(',')}`);
          }
        }

        if (queryParams.length > 0) {
          url += `&${queryParams.join('&')}`;
        }

        console.log('API URL:', url);

        // Send GET request without body
        const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);

        return response;
      } catch (err) {
        return null;
      }
    },
    [actualDiagramId]
  );

  const patchSolution = async (data: any) => {
    const payload = {
      roi: data.roi,
      ai_enablement: data?.agent?.ai_enablement ?? {},
      impact_value: data.impact,
      complexity: data.complexity,
      roi_description: data.roi_description,
      complexity_description: data.complexity_description,
      description: data.description,
      description_llm: data.description_llm,
      llm_agent: data.llm_agent || [],
      solution_enabler: data.solution_enabler,
      benefits: data.benefits,
      data_sources: data.data_sources,
      key_requirements: data.key_requirements || [],
      diagram_id: data.diagram_id,
      embedded: data.agent?.embedded || [],
      ai_solution: data.agent?.ai_solution || [],
      organization_id: currentOrg,
      solution_id: selectedSolutionFromLibrary.id.toString(),
      name: data.opportunity_name || '',
    };

    if (!payload?.ai_enablement || Object.keys(payload?.ai_enablement ?? {})?.length === 0) {
      delete payload.ai_enablement;
    }
    if (!payload.embedded || payload.embedded.length === 0) {
      delete payload.embedded;
    } else if (
      typeof payload?.embedded === 'object' ||
      Object.keys(payload.embedded || {}).length === 0
    ) {
      delete payload.embedded;
    }

    const url = `${API_GRAPH_BASE}/custom-process/ai-opportunity?organization_id=${currentOrg}&solution_id=${selectedSolutionFromLibrary.id}`;
    const response = await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, payload);

    const hasAppliedFilters = Object.values(appliedFilters).some((category) =>
      Object.values(category).some(Boolean)
    );
    await fetchAISolution(hasAppliedFilters ? appliedFilters : undefined);

    return response;
  };

  const deleteSolution = async (id: string) => {
    try {
      const url = `${API_GRAPH_BASE}/custom-process/ai-opportunity?organization_id=${currentOrg}&solution_id=${id}`;
      const response = await httpService<any>(httpConstants.METHOD_TYPE.DELETE, url);

      setSolutionData((prevData) => prevData.filter((solution) => solution.id !== id));

      if (selectedSolutionFromLibrary.id === id) {
        setSelectedSolutionFromLibrary({ id: '', name: '' });
        setAiSolutionData(null);
      }

      setShowDeleteModal(false);

      dispatch(refreshTaxonomy(true));

      const hasAppliedFilters = Object.values(appliedFilters).some((category) =>
        Object.values(category).some(Boolean)
      );
      await fetchAISolution(hasAppliedFilters ? appliedFilters : undefined);

      ShowToast({ message: 'Solution deleted successfully', type: ToastTypeEnum.Success });

      return response;
    } catch (error) {
      console.error('Error deleting solution:', error);
      ShowToast({ message: 'Error deleting solution', type: ToastTypeEnum.Error });
      setShowDeleteModal(false);
    }
  };

  const loadSolutions = async () => {
    setInternalLoading(true);
    try {
      const hasAppliedFilters = Object.values(appliedFilters).some((category) =>
        Object.values(category).some(Boolean)
      );
      const response = await fetchAISolution(hasAppliedFilters ? appliedFilters : undefined);

      if (response?.results?.length > 0) {
        const mapped = response.results.map(
          (
            {
              id,
              opportunity_name,
              roi,
              complexity,
              impact,
              vault,
              solution_enabler,
              status,
              description,
            }: any,
            index: number
          ) => ({
            id,
            serial: index + 1,
            opportunity_name,
            roi: roi || '-',
            complexity: complexity || '-',
            impact: impact || '-',
            vault: vault || false,
            solution_enabler: solution_enabler || 'N/A',
            approval_status: status || null,
            description: description || null,
          })
        );

        setOriginalSolutionData(mapped);
        setFilteredData(mapped);
        sortSolutions(mapped);
        setDataSourceType('rest');
        setInternalLoading(false);
        return;
      }
      if (hasAppliedFilters) {
        setSolutionData([]);
        setDataSourceType('rest');
        setInternalLoading(false);
        return;
      }
      if (data?.aiSolution?.fetch?.length) {
        const mapped = data.aiSolution.fetch.map(
          ({ id, name, impactAssessment }: any, index: number) => ({
            id,
            serial: index + 1,
            opportunity_name: name,
            impact: impactAssessment?.general ?? 'N/A',
          })
        );
        setOriginalSolutionData(mapped);
        setFilteredData(mapped);
        sortSolutions(mapped);
        setDataSourceType('graphql');
        setInternalLoading(false);
      } else if (!qlLoading) {
        setSolutionData([]);
        setDataSourceType(null);
        setInternalLoading(false);
      }
    } catch (error) {
      console.error('Error loading solutions:', error);
      ShowToast({ message: 'Error loading solutions', type: ToastTypeEnum.Error });
      setSolutionData([]);
      setDataSourceType(null);
      setInternalLoading(false);
    }
  };

  const hasAnyFiltersApplied = () => {
    return Object.values(appliedFilters).some((category) => Object.values(category).some(Boolean));
  };

  useEffect(() => {
    loadSolutions();
  }, [fetchAISolution, data, qlLoading, refreshKey, actualDiagramId]);

  // Re-sort filteredData when primarySort changes or filteredData updates
  useEffect(() => {
    if (filteredData.length === 0) return;
    sortSolutions(filteredData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primarySort, filteredData]);

  const handleVaultChange = async (id: string, state: boolean) => {
    const url = `${API_BASE}/api/v1/e2e_taxonomy/new-vault/`;
    try {
      const response = await httpService<any>(
        state ? httpConstants.METHOD_TYPE.POST : httpConstants.METHOD_TYPE.DELETE,
        url,
        {
          organization_id: currentOrg,
          ai_solution_id: id,
        }
      );
      return !!response;
    } catch (error) {
      console.error('Vault update failed:', error);
      return false;
    }
  };

  const handleToggleVault = async () => {
    if (!selectedSolutionFromLibrary || !selectedSolutionFromLibrary.id) return;

    const newState = !selectedSolutionFromLibrary.vault;
    const success = await handleVaultChange(selectedSolutionFromLibrary.id, newState);

    if (success) {
      setSelectedSolutionFromLibrary({ ...selectedSolutionFromLibrary, vault: newState });

      setSolutionData((prevData) =>
        prevData.map((solution) =>
          solution.id === selectedSolutionFromLibrary.id
            ? { ...solution, vault: newState }
            : solution
        )
      );

      if (newState) {
        ShowToast({ message: 'Solution added to list', type: ToastTypeEnum.Success });
      } else {
        ShowToast({ message: 'Solution removed from list', type: ToastTypeEnum.Success });
      }
    } else {
      ShowToast({ message: 'Unable to process request', type: ToastTypeEnum.Error });
    }
    setShowVaultModal(false);
  };

  const handleCloseVaultModal = () => setShowVaultModal(false);

  const handleSave = async (data: any) => {
    setAiSolutionData(data);

    // Store roi and complexity as plain strings to match the format in loadSolutions
    setSolutionData((prevData) =>
      prevData.map((solution) =>
        solution.id === selectedSolutionFromLibrary.id
          ? {
              ...solution,
              serial: solution.serial,
              agent: data.agent,
              opportunity_name: data.opportunity_name || solution.opportunity_name,
              roi: data.roi ? String(data.roi).toUpperCase() : '-',
              complexity: data.complexity ? String(data.complexity).toUpperCase() : '-',
              impact: data.impact || solution.impact,
              solution_enabler: data.solution_enabler || solution.solution_enabler,
              approval_status: data.status || solution.approval_status,
              description: data.description || solution.description,
              vault: data.vault !== undefined ? data.vault : solution.vault,
            }
          : solution
      )
    );

    try {
      await patchSolution(data);

      const updatedSolutionData = await fetchAISolutionByID({
        solutionId: selectedSolutionFromLibrary.id,
        diagramId: actualDiagramId,
        organizationId: currentOrg,
      });

      if (updatedSolutionData) {
        setAiSolutionData(updatedSolutionData);

        // Update solutionData with the fresh API response to ensure consistency
        setSolutionData((prevData) =>
          prevData.map((solution) =>
            solution.id === selectedSolutionFromLibrary.id
              ? {
                  ...solution,
                  roi: (updatedSolutionData as any)?.roi
                    ? String((updatedSolutionData as any).roi).toUpperCase()
                    : '-',
                  complexity: (updatedSolutionData as any)?.complexity
                    ? String((updatedSolutionData as any).complexity).toUpperCase()
                    : '-',
                  impact: (updatedSolutionData as any)?.impact || solution.impact,
                  approval_status: (updatedSolutionData as any)?.status || solution.approval_status,
                  description: (updatedSolutionData as any)?.description || solution.description,
                  vault:
                    (updatedSolutionData as any)?.vault !== undefined
                      ? (updatedSolutionData as any).vault
                      : solution.vault,
                }
              : solution
          )
        );
      }

      ShowToast({ message: 'Solution updated successfully', type: ToastTypeEnum.Success });
    } catch (error) {
      console.error('Error updating solution:', error);
      ShowToast({ message: 'Error updating solution', type: ToastTypeEnum.Error });
    }
  };

  const handleUpdateSolution = (data: any) => {
    setAiSolutionData(data);
    setSolutionData((prevData) =>
      prevData.map((solution) =>
        solution.id === selectedSolutionFromLibrary.id
          ? {
              ...solution,
              approval_status: data.status || solution.approval_status,
            }
          : solution
      )
    );
  };
  const handleUpdateRenameProcess = async (newName: string) => {
    try {
      const url = `${API_BASE}/api/v1/e2e_taxonomy/level2/${selectedCard?.id}/`;
      const payload = { name: newName };

      await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, payload);
      setCurrentProcessName(newName);
      dispatch(updateIdeationFieldState({ field: 'selectedProcess', value: newName }));
      setRenameProcessName(newName);
      dispatch(refreshTaxonomy(true));

      ShowToast({ message: 'Process name updated successfully', type: ToastTypeEnum.Success });
    } catch (error) {
      console.error('Error updating process name:', error);
      ShowToast({ message: 'Failed to update process name', type: ToastTypeEnum.Error });
    }
  };

  useEffect(() => {
    if (showEditConfirmModal) {
      setRenameProcessName(
        selectedCard?.opportunity?.items?.[0]?.opportunity_title ||
          selectedCard?.name ||
          selectedProcess ||
          ''
      );
    }
  }, [showEditConfirmModal]);

  const handleRemoveClick = async () => {
    try {
      if (aiSolutionData.status === 'created') {
        const url = `${API_BASE}/api/v1/e2e_taxonomy/opportunities/${aiSolutionData.id}/`;
        await httpService(httpConstants.METHOD_TYPE.DELETE, url);
        setSelectedSolutionFromLibrary({ id: '', name: '' });
      }
      ShowToast({
        type: ToastTypeEnum.Success,
        message: 'Process removed successfully',
      });
    } catch (error) {
      console.error('Error removing solution:', error);
      ShowToast({
        type: ToastTypeEnum.Error,
        message: 'Error removing process',
      });
    } finally {
      loadSolutions();
      handleScrollToTop();
    }
  };

  const handleRenameProcess = () => {
    setRenameProcessName(selectedCard?.name || selectedProcess || '');
    setShowEditConfirmModal(true);
  };

  const handleDeleteIdeation = () => {
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDeleteIdeation = async () => {
    try {
      const caseIdToUse = selectedCardUseCaseId || useCaseId;

      if (!caseIdToUse) {
        ShowToast({ message: 'Use case ID not found', type: ToastTypeEnum.Error });
        return;
      }

      const url = `${API_BASE}/api/v1/ideation/use-cases/${caseIdToUse}/delete/`;
      const response = await httpService<any>(httpConstants.METHOD_TYPE.DELETE, url);

      if (response) {
        ShowToast({ message: 'Ideation deleted successfully', type: ToastTypeEnum.Success });
        setShowDeleteConfirmModal(false);

        if (setActiveStep) {
          setActiveStep('dashboard');
        }
      } else {
        ShowToast({ message: 'Failed to delete ideation', type: ToastTypeEnum.Error });
      }
    } catch (error) {
      console.error('Error deleting ideation:', error);
      ShowToast({ message: 'Error deleting ideation', type: ToastTypeEnum.Error });
      setShowDeleteConfirmModal(false);
    }
  };

  // useEffect(() => {
  //   if (!state?.cameFromDashboardDetails) return;
  //   if (!solutionData || solutionData.length === 0) return;

  //   const filteredSolution = solutionData.find((item) => item.id === state.opportunity.id);

  //   if (!filteredSolution) return;

  //   setSelectedSolutionFromLibrary({
  //     id: filteredSolution.id,
  //     name: filteredSolution.opportunity_name,
  //     vault: filteredSolution.vault,
  //     solution_enabler: filteredSolution.solution_enabler || undefined,
  //   });

  //   setAiSolutionData({
  //     id: filteredSolution.id,
  //     opportunity_name: filteredSolution.opportunity_name,
  //     roi: filteredSolution.roi,
  //     impact: filteredSolution.impact,
  //     complexity: filteredSolution.complexity,
  //     description: filteredSolution.description,
  //     roi_description: '',
  //     complexity_description: '',
  //     benefits: {},
  //     data_sources: {
  //       internal: { structured: [], unstructured: [] },
  //       external: { structured: [], unstructured: [] },
  //     },
  //     key_requirements: [],
  //     llm_agent: [],
  //     solution_enabler: filteredSolution.solution_enabler,
  //     diagram_id: actualDiagramId,
  //     organization_id: currentOrg,
  //     status: filteredSolution.approval_status || null,
  //   });

  //   setDataSourceType('rest');
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [state, solutionData]);

  const processReportData = async () => {
    try {
      const caseIdToUse = selectedCardUseCaseId || useCaseId;

      if (!caseIdToUse) {
        ShowToast({ message: 'Use case ID not found', type: ToastTypeEnum.Error });
        return;
      }

      const ideationDetails = await httpService<any>(
        httpConstants.METHOD_TYPE.GET,
        `${API_BASE}/api/v1/ideation/use-cases/${caseIdToUse}/`
      );

      const proposedEnhancementsFromApiRaw = ideationDetails?.proposed_enhancements?.items ?? [];

      const proposedEnhancements: Enhancement[] = Array.isArray(proposedEnhancementsFromApiRaw)
        ? proposedEnhancementsFromApiRaw.map((item: any, index: number) => ({
            id: item.id ?? String(index),
            enhancement_title: item.opportunity_title || item.title || 'AI Enhancement Opportunity',
            enhancement_description:
              item.opportunity_description || item.description || 'Description not available',
          }))
        : [];

      // Get diagram ID from diagramData
      const diagramId =
        (diagramData as any)?.diagram?.id ||
        (diagramData as any)?.DiagramData?.diagram?.id ||
        actualDiagramId;

      // Fetch detailed solution data for all AI opportunities using diagram_id
      const opportunitiesIDsResponse = await httpService<{ results: any[] }>(
        httpConstants.METHOD_TYPE.GET,
        `${API_BASE}/api/v1/e2e_taxonomy/opportunities/?diagram_id=${diagramId}`
      );

      const opportunitiesIDs = opportunitiesIDsResponse?.results.map((item: any) => item.id) ?? [];

      const completeSolutionData = await Promise.all(
        opportunitiesIDs.map(
          async (id: string) =>
            await fetchAISolutionWithAgentEnablement({
              solutionId: String(id),
              diagramId: diagramId,
              organizationId: currentOrg,
            })
        )
      );

      const validSolutionData = completeSolutionData.filter(
        (item): item is NonNullable<(typeof completeSolutionData)[number]> => item != null
      );

      validSolutionData.forEach((item: any) => {
        const custom = item?.agent?.ai_enablement?.enablement_matrix?.custom ?? [];
        const embedded = item?.agent?.ai_enablement?.enablement_matrix?.embedded ?? [];
        const extended = item?.agent?.ai_enablement?.enablement_matrix?.extended ?? [];

        item.agent_data = {
          custom,
          embedded,
          extended,
        };
      });

      // Get diagram XML from diagramData
      const diagramXML =
        (diagramData as any)?.diagram?.xml || (diagramData as any)?.DiagramData?.diagram?.xml || '';

      let diagram_image_base64 = '';
      if (diagramXML) {
        try {
          diagram_image_base64 = await convertBpmnXmlToBase64(diagramXML, 8);
        } catch (conversionError) {
          console.error('Failed to convert BPMN XML to base64 image:', conversionError);
        }
      }

      // Build deep-link URL for the diagram (encoded diagramId and processId).
      const processId = selectedCard?.id ?? '';
      let diagramUrl = '';
      if (diagramId) {
        const origin = window.location.origin;
        const query = [
          `diagramId=${encodeURIComponent(diagramId)}`,
          ...(processId ? [`processId=${encodeURIComponent(processId)}`] : []),
        ].join('&');
        diagramUrl = `${origin}/diagram?${query}`;
      }

      let modifiedSubTitle =
        (diagramData as any)?.useCaseData?.Opportunity_name ||
        selectedCard?.name ||
        'Detailed Opportunity Report';

      const opportunityName = (diagramData as any)?.useCaseData?.Opportunity_name;
      if (typeof opportunityName === 'string') {
        const regex = /^([A-Za-z]{3}-\d+):\s*(.*)$/;
        const match = opportunityName.match(regex);
        if (match && match[2]) {
          modifiedSubTitle = match[2];
          if (modifiedSubTitle.length > 0) {
            modifiedSubTitle = modifiedSubTitle[0].toUpperCase() + modifiedSubTitle.slice(1);
          }
        }
      }

      const payload: Record<string, unknown> = {
        subtitle: modifiedSubTitle,
        proposedEnhancements,
        validSolutionData,
        diagramXML: diagram_image_base64,
        diagramUrl,
        link: {
          text: 'View Diagram',
          url: diagramUrl,
        },
      };

      return payload;
    } catch (error) {
      console.error('Error processing report data:', error);
      throw error;
    }
  };

  const handleDownloadReport = async () => {
    setLoadingDocument(true);
    setDownloadProgressOpen(true);
    setDownloadProgress(0);
    try {
      const templateUrl = '/docx/ideation-report-new.docx';

      setDownloadProgress(30);
      const basePayload = await processReportData();

      if (!basePayload) {
        throw new Error('Failed to process report data');
      }

      setDownloadProgress(60);
      let wordBlob = await generateWordWithAccurateTOC(templateUrl, basePayload);

      if (!wordBlob) {
        throw new Error('Failed to generate Word document');
      }

      // CONDITIONAL: Only update links if diagramUrl exists
      if (basePayload.diagramUrl && typeof basePayload.diagramUrl === 'string') {
        console.log('Updating document links with:', basePayload.diagramUrl);
        wordBlob = await updateWordDocLinks(wordBlob, [
          {
            oldUrl: REPORT_PROCESS_DIAGRAM_REPLACABLE_LINK,
            newUrl: basePayload.diagramUrl,
          },
        ]);
      }

      setDownloadProgress(90);
      const url = window.URL.createObjectURL(wordBlob);
      const link = document.createElement('a');
      link.href = url;
      const fileName =
        (diagramData as any)?.useCaseData?.Opportunity_name ||
        selectedCard?.name ||
        'Detailed Opportunity Report';
      link.setAttribute('download', `${fileName}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setDownloadProgress(100);
      setTimeout(() => {
        setDownloadProgressOpen(false);
        setDownloadProgress(0);
      }, 1500);
    } catch (err) {
      console.error('Word document download failed', err);
      setDownloadProgressOpen(false);
      setDownloadProgress(0);
      ShowToast({ message: 'Failed to download report', type: ToastTypeEnum.Error });
    } finally {
      setLoadingDocument(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '0',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        overflowY: 'auto',
        backgroundColor: '#FFFFFF',
        pb: 14,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'start', mb: 3, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="h6" sx={{ fontSize: '24px', fontWeight: 500 }}>
              {currentProcessName || selectedProcess || 'AI Opportunities'} ({solutionData.length})
            </Typography>
            {isProcess ? (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#666666',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {selectedCard?.name}
              </Typography>
            ) : (
              (selectedCard?.taxonomy || formState?.taxonomy) &&
              [
                selectedCard?.taxonomy?.domain || formState?.domain,
                selectedCard?.taxonomy?.function || formState?.selectedFunction,
                selectedCard?.taxonomy?.process_group || formState?.selectedProcessGroup,
                selectedCard?.taxonomy?.process || formState?.selectedProcess,
              ].filter(Boolean).length > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#666666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {[
                    selectedCard?.taxonomy?.domain || formState?.domain,
                    selectedCard?.taxonomy?.function || formState?.selectedFunction,
                    selectedCard?.taxonomy?.process_group || formState?.selectedProcessGroup,
                    currentProcessName ||
                      selectedCard?.taxonomy?.process ||
                      formState?.selectedProcess,
                  ]
                    .filter(Boolean)
                    .join(' / ')}
                </Typography>
              )
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Subtle filter icon placed next to title (doesn't change layout) */}
          <IconButton
            size="small"
            onClick={handleOpenFilterDrawer}
            sx={{
              border: '1px solid transparent',
              '&:hover': { backgroundColor: 'transparent' },
              ml: 0.5,
              position: 'relative',
            }}
            title="Sort / Filters"
          >
            {hasAnyFiltersApplied() && (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    width: 8,
                    height: 8,
                    backgroundColor: 'red',
                    borderRadius: '50%',
                  }}
                />
              </>
            )}
            <img src={'/images/ideation-filter.svg'} alt={'filter'} height={26} width={26} />
          </IconButton>

          {/* Filter Menu */}

          {/* Filter Drawer - Add this after your main content Drawer */}
          <Drawer
            open={showFilterDrawer}
            onClose={handleCloseFilterDrawer}
            anchor="right"
            hideBackdrop
            sx={{
              '& .MuiDrawer-paper': {
                width: '360px',
                p: 3,
                marginTop: '80px',
                height: 'calc(90vh - 60px)',
                marginRight: '20px',
                borderRadius: '8px',
                marginBottom: '100px',
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Typography sx={{ fontFamily: 'Poppins', fontSize: '18px', fontWeight: 600 }}>
                  Filter
                </Typography>
                <CloseIcon
                  onClick={handleCloseFilterDrawer}
                  sx={{
                    cursor: 'pointer',
                    fontSize: '20px',
                    '&:hover': { opacity: 0.7 },
                  }}
                />
              </Box>

              {/* Filter Sections */}
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {/* ROI Filter */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      fontFamily: 'Poppins',
                      mb: 1.5,
                      color: '#131426',
                    }}
                  >
                    ROI
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {['High', 'Medium', 'Low'].map((option) => (
                      <Box key={option} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <input
                          type="checkbox"
                          checked={
                            filterValues.roi[
                              option.toLowerCase() as keyof typeof filterValues.roi as
                                | 'high'
                                | 'medium'
                                | 'low'
                            ]
                          }
                          onChange={() => handleFilterChange('roi', option)}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#1A55F9',
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '16px',
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontStyle: 'Regular',
                            color: '#000000',
                          }}
                        >
                          {option}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Complexity Filter */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      fontFamily: 'Poppins',
                      mb: 1.5,
                      color: '#131426',
                    }}
                  >
                    Complexity
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {['High', 'Medium', 'Low'].map((option) => (
                      <Box key={option} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <input
                          type="checkbox"
                          checked={
                            filterValues.complexity[
                              option.toLowerCase() as keyof typeof filterValues.complexity as
                                | 'high'
                                | 'medium'
                                | 'low'
                            ]
                          }
                          onChange={() => handleFilterChange('complexity', option)}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#1A55F9',
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '16px',
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontStyle: 'Regular',
                            color: '#000000',
                          }}
                        >
                          {option}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Type Filter */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      fontFamily: 'Poppins',
                      mb: 1.5,
                      color: '#131426',
                    }}
                  >
                    Impact Type
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {['Transformative', 'Breakthrough', 'Incremental'].map((option) => (
                      <Box key={option} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <input
                          type="checkbox"
                          checked={
                            filterValues.type[
                              option.toLowerCase() as keyof typeof filterValues.type as
                                | 'transformative'
                                | 'breakthrough'
                                | 'incremental'
                            ]
                          }
                          onChange={() => handleFilterChange('type', option)}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#1A55F9',
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '16px',
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontStyle: 'Regular',
                            color: '#000000',
                          }}
                        >
                          {option}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Footer Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  pt: 2,
                  mt: 'auto',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleClearAll}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    borderColor: '#E0E0E0',
                    color: '#1A55F9',
                    borderRadius: '6px',
                    py: 1.5,
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: '#1A55F9',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Clear all
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddFilter}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    bgcolor: '#2054F4',
                    borderRadius: '6px',
                    py: 1.5,
                    fontWeight: 500,
                    '&:hover': { bgcolor: '#1A45C6' },
                  }}
                >
                  Apply filter
                </Button>
              </Box>
            </Box>
          </Drawer>
          {!isProcess && (
            <IdeationMenu
              onEdit={handleRenameProcess}
              onDelete={handleDeleteIdeation}
              onDownload={handleDownloadReport}
              editLabel="Rename Process"
              deleteLabel="Delete Ideation"
              disableDownload={loadingDocument}
            />
          )}

          {/* <CloseIcon
            onClick={() => setActiveStep?.('dashboard')}
            sx={{
              cursor: 'pointer',
              transition: '0.2s ease',
              padding: '1px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: '6px',
              },
            }}
          /> */}
        </Box>
      </Box>
      {/* Grid Container for 2 Column Layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(600px, 1fr))',
          gap: 2,
          width: '100%',
        }}
      >
        {solutionData.map((solution) => (
          <Box key={solution.id} sx={{ display: 'flex' }}>
            <IdeationOpportunitiesCard
              title={solution.opportunity_name}
              tag={solution.impact || undefined}
              description={solution.description || 'No description available'}
              complexity={
                typeof solution.complexity === 'string'
                  ? ((solution.complexity.charAt(0).toUpperCase() +
                      solution.complexity.slice(1).toLowerCase()) as 'High' | 'Medium' | 'Low')
                  : 'Medium'
              }
              roi={
                typeof solution.roi === 'string'
                  ? ((solution.roi.charAt(0).toUpperCase() +
                      solution.roi.slice(1).toLowerCase()) as 'High' | 'Medium' | 'Low')
                  : 'Medium'
              }
              approvalStatus={
                (solution.approval_status === 'pending'
                  ? 'approval_pending'
                  : solution.approval_status) as 'approved' | 'approval_pending' | 'created' | null
              }
              tagBgColor="#60A5FA"
              tagColor="#FFFFFF"
              vault={solution.vault}
              onClick={() => {
                setSelectedSolutionFromLibrary({
                  id: solution.id,
                  name: solution.opportunity_name,
                  vault: solution.vault,
                  solution_enabler: solution.solution_enabler || undefined,
                });
                setAiSolutionData({
                  id: solution.id,
                  opportunity_name: solution.opportunity_name,
                  roi: solution.roi,
                  impact: solution.impact,
                  complexity: solution.complexity,
                  description: solution.description,
                  roi_description: '',
                  complexity_description: '',
                  benefits: {},
                  data_sources: {
                    internal: { structured: [], unstructured: [] },
                    external: { structured: [], unstructured: [] },
                  },
                  key_requirements: [],
                  llm_agent: [],
                  solution_enabler: solution.solution_enabler,
                  diagram_id: actualDiagramId,
                  organization_id: currentOrg,
                  status: solution.approval_status || null,
                });
                setDataSourceType('rest');
              }}
              clickable={true}
            />
          </Box>
        ))}
      </Box>
      {/* Empty State */}
      {solutionData.length === 0 && !combinedLoading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No AI Opportunities Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI opportunities will appear here once they are generated
          </Typography>
        </Box>
      )}
      {/* Loading State */}
      {combinedLoading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '700px',
          }}
        >
          <Loader message="Loading AI Opportunities..." fullScreen={false} />
        </Box>
      )}

      {/* Rest of your modals and drawer code remains the same */}
      <Drawer
        open={!!selectedSolutionFromLibrary.id}
        onClose={async () => {
          setSelectedSolutionFromLibrary({ id: '', name: '' });
          if (needsRefetchOnClose) {
            setNeedsRefetchOnClose(false);
            try {
              const response = await fetchAISolution();
              if (response?.results?.length > 0) {
                const mapped = response.results.map(
                  (
                    {
                      id,
                      opportunity_name,
                      roi,
                      complexity,
                      impact,
                      vault,
                      solution_enabler,
                      status,
                      description,
                    }: any,
                    index: number
                  ) => ({
                    id,
                    serial: index + 1,
                    opportunity_name,
                    roi: roi || '-',
                    complexity: complexity || '-',
                    impact: impact || '-',
                    vault: vault || false,
                    solution_enabler: solution_enabler || 'N/A',
                    approval_status: status || null,
                    description: description || null,
                  })
                );
                sortSolutions(mapped);
                setDataSourceType('rest');
              } else if (data?.aiSolution?.fetch?.length) {
                const mapped = data.aiSolution.fetch.map(
                  ({ id, name, impactAssessment }: any, index: number) => ({
                    id,
                    serial: index + 1,
                    opportunity_name: name,
                    impact: impactAssessment?.general ?? 'N/A',
                  })
                );
                sortSolutions(mapped as AiSolutionRow[]);
                setDataSourceType('graphql');
              } else {
                setSolutionData([]);
                setDataSourceType(null);
              }
            } catch (err) {
              console.error('Error refetching solutions on close:', err);
            }
          }
        }}
        anchor="right"
        hideBackdrop
        ModalProps={{
          keepMounted: true,
          sx: {
            zIndex: 1400,
            '& .MuiDrawer-paper': {
              width: { xs: '100%', md: '70%' },
              pointerEvents: 'auto',
            },
          },
        }}
      >
        <Box>
          {dataSourceType === 'rest' && (
            <ViewSolutionDetails
              selectedSolutionId={selectedSolutionFromLibrary.id}
              selectedSolutionFromLibrary={selectedSolutionFromLibrary}
              enterpriseContextApplied={enterpriseContextApplied}
              setEnterpriseContextApplied={setEnterpriseContextApplied}
              aiSolutionData={aiSolutionData}
              setAiSolutionData={setAiSolutionData}
              diagramId={actualDiagramId}
              handleToggleVault={handleToggleVault}
              handleUpdateSolution={handleUpdateSolution}
              handleSave={handleSave}
              isProcessDiagramActive={true}
              handleRemoveClick={handleRemoveClick}
              onApproval={(updated: any) => {
                handleUpdateSolution(updated);
                setNeedsRefetchOnClose(true);
              }}
              closeDrawer={() => setSelectedSolutionFromLibrary({ id: '', name: '' })}
              isIdeation={isProcess ? false : true}
            />
          )}
          {dataSourceType === 'graphql' && (
            <ViewSolutionDetails
              selectedSolutionId={selectedSolutionFromLibrary.id}
              selectedSolutionFromLibrary={selectedSolutionFromLibrary}
              enterpriseContextApplied={enterpriseContextApplied}
              setEnterpriseContextApplied={setEnterpriseContextApplied}
              aiSolutionData={aiSolutionData}
              setAiSolutionData={setAiSolutionData}
              handleToggleVault={handleToggleVault}
              handleUpdateSolution={handleUpdateSolution}
              handleSave={handleSave}
              isProcessDiagramActive={true}
              handleRemoveClick={handleRemoveClick}
              onApproval={(updated: any) => {
                handleUpdateSolution(updated);
                setNeedsRefetchOnClose(true);
              }}
              closeDrawer={() => setSelectedSolutionFromLibrary({ id: '', name: '' })}
              isIdeation={isProcess ? false : true}
            />
          )}
        </Box>
      </Drawer>

      <CustomModal
        isOpen={showVaultModal}
        onClose={handleCloseVaultModal}
        title={selectedSolutionFromLibrary?.vault ? 'Remove from My List' : 'Add to My List'}
        maxWidth="sm"
      >
        <Box
          sx={{
            bgcolor: '#F9FBFF',
            borderRadius: '12px',
            p: 3,
            display: 'flex',
            alignItems: 'end',
            justifyContent: 'stretch',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '16px', color: '#2c2c2c' }}>
              Are you sure you want to{' '}
              {selectedSolutionFromLibrary?.vault
                ? 'remove this AI Solution from'
                : 'add this AI Solution to'}{' '}
              your list?
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleToggleVault}
            sx={{
              textTransform: 'none',
              bgcolor: '#2054F4',
              borderRadius: '6px',
              px: 3,
              py: 1,
              mt: 2,
              '&:hover': { bgcolor: '#1A45C6' },
              fontWeight: 'bold',
            }}
          >
            {selectedSolutionFromLibrary?.vault ? 'Remove' : 'Add'}
          </Button>
        </Box>
      </CustomModal>

      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            width: '400px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: 'auto',
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Typography sx={{ textAlign: 'center' }}>
            Are you sure you want to delete this solution?
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Button variant="contained" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: '#EF4444', '&:hover': { backgroundColor: '#EF4444' } }}
              onClick={() => deleteSolution(solutionToDelete)}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
      {/* Rename Process Modal */}
      <CustomModal
        isOpen={showEditConfirmModal}
        onClose={() => setShowEditConfirmModal(false)}
        title="Rename Process"
        maxWidth="sm"
        width="100%"
      >
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography sx={{ fontSize: '14px', color: '#2c2c2c', mb: 1.5, fontWeight: 500 }}>
              Process Name
            </Typography>
            <input
              type="text"
              value={renameProcessName}
              onChange={(e) => setRenameProcessName(e.target.value)}
              placeholder="Enter process name"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '16px',
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Poppins, sans-serif',
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border-color 0.3s',
              }}
              onFocus={(e) => {
                (e.target as HTMLInputElement).style.borderColor = '#1A55F9';
              }}
              onBlur={(e) => {
                (e.target as HTMLInputElement).style.borderColor = '#E0E0E0';
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => {
                setShowEditConfirmModal(false);
                handleUpdateRenameProcess(renameProcessName);
              }}
              sx={{
                textTransform: 'none',
                bgcolor: '#2054F4',
                borderRadius: '6px',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                fontSize: '14px',
                '&:hover': { bgcolor: '#1A45C6' },
              }}
            >
              Update
            </Button>
          </Box>
        </Box>
      </CustomModal>

      <ConfirmationModal
        isOpen={showDeleteConfirmModal}
        title={`Delete Process`}
        description={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ color: '#4f4f4f' }}>
              <Box>
                Are you sure you want to delete this entire ideation? This action cannot be undone.
                All associated data will be permanently removed.
              </Box>
            </Typography>
          </Box>
        }
        primaryButtonText={
          <Box display="flex" alignItems="center" gap={1}>
            Delete
          </Box>
        }
        onPrimaryAction={handleConfirmDeleteIdeation}
        onClose={() => setShowDeleteConfirmModal(false)}
        width={'781px'}
        outerPadding={0}
      />
      <DownloadProgressSnackbar open={downloadProgressOpen} progress={downloadProgress} />
    </Box>
  );
};

export default AiOpportunities;
